/** Boot: parse /trip/:id + #key, load state from the server, render the app,
 * connect live sync. One delegated listener replaces all inline handlers. */
import "leaflet/dist/leaflet.css";
import "@fontsource-variable/inter";
import "@fontsource-variable/fraunces";
import "./styles/base.css";
import "./styles/app.css";
// Curated prose used to be imported here as four raw HTML blobs and rendered
// only for croatia26. It now travels in the trip definition (def.prose), so
// every trip renders through the same path and no trip carries another's
// content — a Kyoto trip was briefly showing Dubrovnik ferry advice.
import { applySavedSlots, seedSlots } from "@trip/shared/engine/schedule";
import { esc } from "@trip/shared/engine/time";
import type { TripResponse } from "@trip/shared/protocol";
import { isFixed } from "@trip/shared/types";
import { store } from "./store.js";
import { connect, onResync, onStatus } from "./sync.js";
import { accountPanelHTML, fillProviderButtons, openAccountModal, renderAvatarMenu, resetPageHTML, toggleAvatarMenu, wireAccountPanel, wireResetPage } from "./ui/account.js";
import { banner } from "./ui/banner.js";
import { renderChecklist } from "./ui/checklist.js";
import { describeChange, renderStatus, toggleChangelog } from "./ui/changelog.js";
import { openDayMap } from "./ui/daymap.js";
import { buildPrintDocument, printItinerary } from "./ui/print.js";
import { clearDirections, openDirections, switchDirectionsMode } from "./ui/directions.js";
import { closeModal, openModal } from "./ui/modal.js";
import { genericHeroHTML, heroHTML } from "./ui/hero.js";
import { ico } from "./ui/icons.js";
import { initReveal } from "./ui/motion.js";
import { mountRangeCal } from "./ui/rangecal.js";
import { buildDayNav, buildDays, initDayNavSpy } from "./ui/render.js";
import { openActivitiesPanel, offerActivity, removeActivity, unofferActivity } from "./ui/actpanel.js";
import { choosePickerActivity, openActivityPicker } from "./ui/actpicker.js";
import { addSelectedImports, addSelectedProposals, checkAiEnabled, openAiImport, openAiSuggest } from "./ui/aisuggest.js";
import { showThinkOverlay, hideThinkOverlay } from "./ui/thinkoverlay.js";
import { isEditing, moveDay, moveItem, openDayForm, openItemForm, removeItem, toggleEditing } from "./ui/editor.js";
import { openActForm, openBlForm, openLodgings, openStayForm, openTripSettings, removeStay, setDayHome } from "./ui/editmore.js";
import { canShare, copyShareLink, openShare, revokeShareKey } from "./ui/share.js";
import { onAct, onChecklistToggle, onDayTarget, onDur, onFitDay, onFixDur, onSt, refreshSlot } from "./ui/slots.js";
import { currentTrip, invalidateTrip, recomputeAndRender } from "./ui/times.js";
import { initTripMap } from "./ui/tripmap.js";
import { exportWanderlog } from "./ui/wanderlog.js";

const app = document.getElementById("app")!;

const themeNow = () => document.documentElement.dataset.theme === "dark";

const actionBarHtml = `
  <div class="actionbar">
    <button class="btn-det btn-ico" data-action="theme" id="themebtn" title="Switch between light and dark">${themeNow() ? ico.sun : ico.moon}</button>
    <button class="btn-det" data-action="print" title="Print-friendly paper backup">${ico.print} Print</button>
    <button class="btn-det" data-action="wanderlog" title="Copy your selected activities as a per-day place list — paste into Wanderlog's add-places box">${ico.export} Wanderlog</button>
    <button class="btn-det" data-action="changelog" title="Who changed what, when">${ico.history} Changes</button>
    <button class="btn-det" data-action="activities" title="Every activity in the trip — search, and manage which slots offer it">🗂 Activities</button>
    <button class="btn-det aibtn" data-action="ai-import" style="display:none" title="Paste an itinerary; the agent extracts places for review">${ico.import} Import</button>
    <button class="btn-det" data-action="ed-toggle" id="editbtn" style="display:none" title="Edit days, stops, and slots">✎ Edit</button>
    <button class="btn-det ed-only" data-action="ed-lodgings" style="display:none" title="Stays and each day's base">🏨 Lodgings</button>
    <button class="btn-det ed-only" data-action="ed-trip-set" style="display:none" title="Trip-wide settings">⚙ Settings</button>
    <button class="btn-det" data-action="share" id="sharebtn" style="display:none" title="Invite links for this trip">${ico.users} Share</button>
  </div>
  <div id="changelog" class="changelog" style="display:none"></div>
  <div id="verbanner" style="display:none; border-radius:8px; padding:8px 12px; font-size:12.5px; margin:0 0 16px;"></div>`;

const booklistHtml = `
  <div class="booklist" id="booklist">
    <h3>📋 Booking checklist</h3>
  </div>`;

/**
 * Definition-carried info cards (tips, culture, look-outs) — the generated
 * trips' counterpart of Croatia's curated prose. Items are ESCAPED without
 * exception: this content can arrive via the AI, and structured-text-only is
 * the contract that keeps that safe.
 */
const sectionsHtml = () =>
  (store.def.sections ?? [])
    .map(
      (s) => `
    <details>
      <summary class="booklist-summary">${esc(s.title)}</summary>
      <div class="booklist trip-section">
        <ul class="ts-list">${s.items.map((i) => `<li>${esc(i)}</li>`).join("")}</ul>
      </div>
    </details>`
    )
    .join("");

/**
 * The trip map — every trip gets one. It was previously inside the curated
 * mapcard.html, which only croatia26 imported, so `#map` never existed for
 * any other trip and initTripMap() silently returned early. The map code
 * itself was already trip-agnostic; only this markup was missing.
 */
const mapcardHtml = `
  <details>
    <summary class="booklist-summary">🗺️ Trip map</summary>
    <div class="booklist mapcard">
      <div class="mapfilters" id="mapfilters"></div>
      <div id="map"></div>
      <div class="mapnote">Numbered pins = your booked beds, in trip order (dashed line = your ferry route). Colored dots = activity options — click one for price + the ⓘ details card. Pin positions are close-but-approximate; exact addresses live in each details card.</div>
    </div>
  </details>`;

/**
 * Curated prose cards carried by the definition, rendered as RAW HTML.
 *
 * Safe because of where it can come from, not because of what it says: no
 * def-op writes `prose`, so neither an AI surface nor a co-planner's editor
 * can put markup here — only `npm run extract` and the admin import that
 * ships its output. AI-authored content goes through sectionsHtml() above,
 * which escapes. See the contract on TripDefinition.prose.
 */
const proseHtml = () => (store.def.prose ?? []).map((p) => p.html).join("");

function renderShell() {
  app.innerHTML = `
    ${heroHTML(store.def.hero, store.tripName)}
    <nav class="daynav" id="daynav"></nav>
    <main>
      ${actionBarHtml}
      ${booklistHtml}
      ${mapcardHtml}
      ${proseHtml()}
      ${sectionsHtml()}
      <div id="days"></div>
    </main>
    <div class="modal-bg" id="modalbg"><div class="modal" id="modal"></div></div>
    <div id="think-overlay" style="display:none">
      <div class="think-spin">✨</div>
    </div>
    <footer>Get A Map</footer>`;
}

// provider buttons render through fillProviderButtons (account.ts) — email +
// password stays the primary path on every surface, providers are the "or"

/** Homepage: signed-in members see their trip list; everyone else signs in. */
async function renderHome() {
  let me: { name?: string; email?: string } | null = null;
  try {
    ({ user: me } = (await (await fetch("/api/me")).json()) as { user: { name?: string; email?: string } | null });
  } catch {
    /* offline — treated as signed out */
  }

  if (me) {
    const { trips } = (await (await fetch("/api/me/trips")).json()) as {
      trips: { id: string; name: string; role: string; start?: string | null; end?: string | null }[];
    };
    // "2026-09-12" → "Sep 12, 2026"; older definitions carry "Sep 12" already
    const MONS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const fmtDay = (s: string) =>
      /^\d{4}-\d{2}-\d{2}$/.test(s)
        ? `${MONS[Number(s.slice(5, 7)) - 1]} ${Number(s.slice(8, 10))}, ${s.slice(0, 4)}`
        : s;
    const tileDates = (t: { start?: string | null; end?: string | null }) =>
      t.start && t.end
        ? `<div class="trip-tile-dates">📅 ${esc(fmtDay(t.start))} – ${esc(fmtDay(t.end))}</div>`
        : "";
    app.innerHTML = `
      ${genericHeroHTML()}
      <main>
        <div class="booklist" style="padding:26px 24px">
          <div style="display:flex; align-items:center; justify-content:space-between; gap:12px; flex-wrap:wrap">
            <h3 style="padding:0">Your trips</h3>
            <a class="btn-cta" href="/new" style="display:inline-block;padding:7px 14px;font-size:12.5px;text-decoration:none">＋ New trip</a>
          </div>
          <div style="padding:14px 2px; font-size:14px; line-height:1.7">
            ${
              trips.length
                ? `<div class="trip-tiles">` +
                  trips
                    .map(
                      (t) =>
                        `<div class="trip-tile" data-action="trip-open" data-id="${encodeURIComponent(t.id)}" data-name="${esc(t.name)}">
                           <div class="trip-tile-top">
                             <span class="trip-tile-name">${esc(t.name)}</span>
                             <span class="trip-tile-role">${t.role === "edit" ? "can edit" : "view only"}</span>
                           </div>
                           ${tileDates(t)}
                           ${
                             t.role === "edit"
                               ? `<div class="trip-tile-actions">
                                    <button type="button" class="btn-det btn-sm" data-action="trip-invite" data-id="${encodeURIComponent(t.id)}" title="Invite a co-planner — opens the trip's share links">${ico.users} Invite</button>
                                    <button type="button" class="btn-det btn-sm" data-action="trip-rename" data-id="${encodeURIComponent(t.id)}" data-name="${esc(t.name)}">✎ Rename</button>
                                    <button type="button" class="btn-det btn-sm" data-action="trip-delete" data-id="${encodeURIComponent(t.id)}" data-name="${esc(t.name)}">✕ Delete</button>
                                  </div>`
                               : ""
                           }
                         </div>`
                    )
                    .join("") +
                  `</div>`
                : `<div style="color:var(--mut);font-size:13px">No trips yet — start one, or open a shared link and save it to your account.</div>`
            }
          </div>
        </div>
      </main>
      <footer>Get A Map — schedules that reflow around real travel time.</footer>`;
    return;
  }

  app.innerHTML = `
    ${genericHeroHTML()}
    <main>
      <div class="booklist signin-card">
        <h3>🔐 Sign in</h3>
        <div class="signin-body">
          <div id="homenotice" style="display:none;margin-bottom:16px"></div>
          <div id="acctpanel">${accountPanelHTML()}</div>
          <div id="signin" class="signin-providers"></div>
        </div>
      </div>
    </main>
    <footer>Get A Map — schedules that reflow around real travel time.</footer>`;

  // Signing in goes straight to the trip list. Reloading "/" technically
  // lands in the same place, but "go to your trips" is the actual intent and
  // it survives the page being reached with a query string or hash.
  wireAccountPanel(() => {
    location.href = "/";
  });
  void fillProviderButtons("signin");

  // The verification link redirects here with its outcome. There's no toolbar
  // banner on the home page, so say it in place.
  const verify = new URLSearchParams(location.search).get("verify");
  const notice = document.getElementById("homenotice");
  if (verify && notice) {
    const ok = verify === "ok";
    notice.style.display = "";
    notice.className = ok ? "vb ok" : "vb warn";
    notice.textContent = ok
      ? "Email confirmed — you're signed in."
      : "That confirmation link has expired or was already used. Sign in to send a new one.";
  }
}

/** Basic profile: who the account is, and the display name it edits under. */
async function renderProfile() {
  type Me = { id: string; name: string | null; email: string | null };
  let user: Me | null = null;
  try {
    ({ user } = (await (await fetch("/api/me")).json()) as { user: Me | null });
  } catch {
    /* fall through to the signed-out state */
  }
  if (!user) {
    location.href = "/";
    return;
  }
  const trips = await fetch("/api/me/trips")
    .then((r) => r.json())
    .then((j: { trips: unknown[] }) => j.trips.length)
    .catch(() => 0);

  app.innerHTML = `
    ${genericHeroHTML()}
    <main>
      <div class="booklist" style="padding:26px 24px;max-width:480px;margin:0 auto">
        <h3>👤 Your profile</h3>
        <form id="profileform" style="padding:14px 2px" novalidate>
          <label style="display:block;margin-bottom:12px">
            <span style="display:block;font-size:12.5px;font-weight:600;margin-bottom:4px">Display name</span>
            <input id="prof-name" type="text" maxlength="80" value="${esc(user.name ?? "")}" autocomplete="name"
              style="width:100%;min-height:42px;padding:6px 12px;border:1.5px solid var(--line);border-radius:9px;font-size:14px;font-family:var(--font-ui)">
            <span style="display:block;font-size:12px;color:var(--mut);margin-top:4px">Shown on trips you're a member of. Edits made through a shared link keep the link's name.</span>
          </label>
          <div style="margin-bottom:12px;font-size:13.5px">
            <span style="font-weight:600">Email:</span> ${esc(user.email ?? "—")}
          </div>
          <div style="margin-bottom:16px;font-size:13.5px">
            <span style="font-weight:600">Trips on this account:</span> ${trips} — <a href="/">view</a>
          </div>
          <button class="btn-cta" type="submit">Save</button>
          <span id="prof-msg" style="display:none;margin-left:10px;font-size:13px"></span>
        </form>
      </div>
    </main>
    <footer>Get A Map</footer>`;

  document.getElementById("profileform")!.addEventListener("submit", async (e) => {
    e.preventDefault();
    const name = (document.getElementById("prof-name") as HTMLInputElement).value.trim();
    const msg = document.getElementById("prof-msg")!;
    const res = await fetch("/api/me", {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ name }),
    }).catch(() => null);
    msg.style.display = "";
    if (res?.ok) {
      msg.textContent = "Saved.";
      msg.style.color = "var(--ok)";
      void renderAvatarMenu();
    } else {
      msg.textContent = ((await res?.json().catch(() => null)) as { error?: string } | null)?.error ?? "Couldn't save.";
      msg.style.color = "var(--warn)";
    }
  });
}

/** Create a blank trip: name + dates now, everything else once the editor exists. */
async function renderNewTrip() {
  let signedIn = false;
  try {
    signedIn = Boolean(((await (await fetch("/api/me")).json()) as { user: unknown }).user);
  } catch {
    /* treated as signed out */
  }

  app.innerHTML = `
    ${genericHeroHTML()}
    <main>
      <div class="booklist" style="padding:26px 24px;max-width:480px;margin:0 auto">
        <h3>＋ New trip</h3>
        ${
          signedIn
            ? `<form id="newtripform" class="nt-form" novalidate>
                 <label class="nt-field">
                   <span class="nt-lab">Trip name</span>
                   <input id="nt-name" type="text" maxlength="80" placeholder="e.g. Japan · Spring 2027">
                 </label>
                 <div class="nt-field">
                   <span class="nt-lab">Trip days</span>
                   <div id="nt-cal" class="rc"></div>
                   <div id="nt-range" class="nt-range" aria-live="polite"></div>
                   <input id="nt-start" type="hidden"><input id="nt-end" type="hidden">
                 </div>
                 <div class="nt-hint">
                   Each day starts as free time — the day grows slots as you plan it. Activities and places come with the trip editor.
                 </div>
                 <div id="nt-ai" class="nt-ai" style="display:none">
                   <label class="ai-main"><input type="checkbox" id="nt-gen">
                     <span style="font-weight:600">✨ Draft the days with AI</span></label>
                   <div id="nt-genfields" style="display:none">
                     <label class="ed-fld"><span>Destination</span>
                       <input id="nt-dest" maxlength="120" placeholder="e.g. Kyoto and Osaka"></label>
                     <div class="share-fields">
                       <label class="ed-fld" style="flex:1 1 140px"><span>Pace</span>
                         <select id="nt-pace">
                           <option value="relaxed">Relaxed</option>
                           <option value="balanced" selected>Balanced</option>
                           <option value="packed">Packed</option>
                         </select></label>
                       <label class="ed-fld" style="flex:2 1 200px"><span>Interests (optional)</span>
                         <input id="nt-interests" maxlength="300" placeholder="food, temples, hiking…"></label>
                     </div>
                     <button type="button" class="btn-det btn-sm" id="nt-anchor-btn">${ico.calendar} Plan cities day by day (optional)</button>
                     <div id="nt-anchors" style="display:none">
                       <div class="anchor-head"><span></span><span>City</span><span>Hotel (optional)</span><span>Move on</span></div>
                       <div id="nt-anchor-rows"></div>
                       <div class="dir-note">Leave a city blank to let the draft decide. "Move on" marks the day you travel to the NEXT city — morning, afternoon, or evening.</div>
                     </div>
                     <div class="dir-note">The draft fills each day with options to choose between — nothing is confirmed, nothing is booked, and every activity starts as ○ Deciding. Map pins come later, one verified address at a time.</div>
                   </div>
                 </div>
                 <button class="btn-cta" type="submit" id="nt-submit">Create trip</button>
                 <div id="nt-wait" class="gen-wait" style="display:none" role="status">
                   <div class="gen-art"><span class="gen-orb"></span><span class="gen-orb o2"></span><span class="gen-orb o3"></span></div>
                   <div class="gen-status" id="nt-wait-line">Sketching the days…</div>
                   <div class="gen-bar"><span></span></div>
                   <div class="gen-sub">Drafting usually takes a minute or two. Keep this page open and it'll jump straight to the trip — and even if you wander off, the finished draft still lands under “My trips”.</div>
                 </div>
                 <div id="nt-msg" class="nt-msg" style="display:none"></div>
               </form>`
            : `<div style="padding:14px 2px;font-size:14px;line-height:1.6">
                 Creating a trip needs an account, so it has somewhere to live.
                 <div id="acctpanel" style="margin-top:16px">${accountPanelHTML()}</div>
               </div>`
        }
      </div>
    </main>
    <footer>Get A Map</footer>`;

  if (!signedIn) {
    wireAccountPanel(() => location.reload()); // stay on /new — that's what they came to do
    return;
  }

  // the AI-draft option appears only when the server actually has a provider
  void fetch("/api/ai/status")
    .then((r) => r.json())
    .then((d: { enabled?: boolean }) => {
      if (d.enabled) document.getElementById("nt-ai")!.style.display = "";
    })
    .catch(() => {});
  document.getElementById("nt-gen")?.addEventListener("change", (e) => {
    const on = (e.target as HTMLInputElement).checked;
    document.getElementById("nt-genfields")!.style.display = on ? "" : "none";
    document.getElementById("nt-submit")!.textContent = on ? "✨ Draft trip" : "Create trip";
  });

  const startEl = document.getElementById("nt-start") as HTMLInputElement;
  const endEl = document.getElementById("nt-end") as HTMLInputElement;
  const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const fmtIso = (iso: string) => {
    const d = new Date(`${iso}T12:00:00Z`);
    return `${MON[d.getUTCMonth()]} ${d.getUTCDate()}`;
  };

  const collectAnchors = () =>
    [...document.querySelectorAll<HTMLInputElement>("input[data-anchor-city]")]
      .map((cityEl) => {
        const iso = cityEl.dataset.anchorCity!;
        const city = cityEl.value.trim();
        const hotel = document.querySelector<HTMLInputElement>(`input[data-anchor-hotel="${iso}"]`)?.value.trim() ?? "";
        const departure = document.querySelector<HTMLSelectElement>(`select[data-anchor-dep="${iso}"]`)?.value ?? "";
        return { iso, ...(city ? { city } : {}), ...(hotel ? { hotel } : {}), ...(departure ? { departure } : {}) };
      })
      .filter((a) => a.city || a.hotel || a.departure);

  /** Anchor rows from the chosen dates. Values typed so far survive a date
   * change: matching isos keep their city/hotel/departure. */
  function buildAnchorRows(): boolean {
    const start = startEl.value;
    const end = endEl.value;
    if (!start || !end || end < start) return false;
    const keep = new Map(collectAnchors().map((a) => [a.iso, a]));
    const rows: string[] = [];
    const t0 = new Date(`${start}T12:00:00Z`).getTime();
    const n = Math.min(60, Math.round((new Date(`${end}T12:00:00Z`).getTime() - t0) / 86_400_000) + 1);
    for (let i = 0; i < n; i++) {
      const d = new Date(t0 + i * 86_400_000);
      const iso = d.toISOString().slice(0, 10);
      const a = keep.get(iso);
      rows.push(`<div class="anchor-row">
        <span>${DOW[d.getUTCDay()]} ${MON[d.getUTCMonth()]} ${d.getUTCDate()}</span>
        <input data-anchor-city="${iso}" maxlength="60" placeholder="e.g. Kyoto" value="${esc(a?.city ?? "")}">
        <input data-anchor-hotel="${iso}" maxlength="80" value="${esc(a?.hotel ?? "")}">
        <select data-anchor-dep="${iso}">
          <option value="">staying</option>
          ${["morning", "afternoon", "evening"].map((v) => `<option value="${v}"${a?.departure === v ? " selected" : ""}>${v}</option>`).join("")}
        </select></div>`);
    }
    document.getElementById("nt-anchor-rows")!.innerHTML = rows.join("");
    document.getElementById("nt-anchors")!.style.display = "";
    return true;
  }

  // one continuous range calendar drives the hidden date fields
  const rangeOut = document.getElementById("nt-range")!;
  mountRangeCal(document.getElementById("nt-cal")!, (start, end) => {
    startEl.value = start ?? "";
    endEl.value = end ?? "";
    if (start && end) {
      const nights = Math.round((new Date(`${end}T12:00:00Z`).getTime() - new Date(`${start}T12:00:00Z`).getTime()) / 86_400_000);
      rangeOut.textContent = `${fmtIso(start)} → ${fmtIso(end)} · ${nights + 1} day${nights ? "s" : ""}`;
      // a date change reshapes the open anchor grid, keeping what was typed
      if (document.getElementById("nt-anchors")!.style.display !== "none") buildAnchorRows();
    } else if (start) {
      rangeOut.textContent = `${fmtIso(start)} → now pick the last day`;
    } else {
      rangeOut.textContent = "";
    }
  });

  document.getElementById("nt-anchor-btn")?.addEventListener("click", () => {
    if (!buildAnchorRows()) {
      const msg = document.getElementById("nt-msg")!;
      msg.style.display = "";
      msg.textContent = "Pick the trip days on the calendar first.";
    }
  });

  /* the draft-in-progress animation: rotating status lines + a sliding bar */
  const WAIT_LINES = [
    "Sketching the days…",
    "Scouting neighborhoods…",
    "Balancing the pace…",
    "Placing lunches…",
    "Writing tips & look-outs…",
    "Checking the shape of each day…",
  ];
  let waitTimer: ReturnType<typeof setInterval> | null = null;
  let pollTimer: ReturnType<typeof setInterval> | null = null;
  function showWait(on: boolean) {
    document.getElementById("nt-wait")!.style.display = on ? "" : "none";
    (document.getElementById("nt-submit") as HTMLButtonElement).style.display = on ? "none" : "";
    if (waitTimer) {
      clearInterval(waitTimer);
      waitTimer = null;
    }
    if (on) {
      const line = document.getElementById("nt-wait-line")!;
      let i = 0;
      line.textContent = WAIT_LINES[0];
      waitTimer = setInterval(() => {
        i = (i + 1) % WAIT_LINES.length;
        line.textContent = WAIT_LINES[i];
      }, 3500);
    }
  }

  /** A draft cooks server-side; this end just asks how it's going. The job
   * outlives the page — if polling dies, the trip still lands in My trips. */
  function pollDraft(job: string) {
    const started = Date.now();
    let misses = 0;
    const stop = (text?: string) => {
      if (pollTimer) clearInterval(pollTimer);
      pollTimer = null;
      showWait(false);
      if (text) {
        const m = document.getElementById("nt-msg")!;
        m.style.display = "";
        m.textContent = text;
      }
    };
    pollTimer = setInterval(() => {
      void (async () => {
        if (Date.now() - started > 10 * 60_000) {
          stop("Still working — the draft will appear under “My trips” when it lands.");
          return;
        }
        try {
          const res = await fetch(`/api/trips/generate/${encodeURIComponent(job)}`);
          if (res.status === 404) {
            stop("Lost track of the draft — check “My trips” in a minute; it may have landed anyway.");
            return;
          }
          if (!res.ok) throw new Error("poll failed");
          misses = 0;
          const s = (await res.json()) as { status: string; id?: string; error?: string };
          if (s.status === "done" && s.id) {
            if (pollTimer) clearInterval(pollTimer);
            location.href = `/trip/${encodeURIComponent(s.id)}`;
          } else if (s.status === "error") {
            stop(s.error ?? "Drafting failed — try again.");
          }
        } catch {
          if (++misses >= 5) stop("Couldn't reach the server — check “My trips” in a minute; the draft may land anyway.");
        }
      })();
    }, 2500);
  }

  document.getElementById("newtripform")!.addEventListener("submit", async (e) => {
    e.preventDefault();
    const name = (document.getElementById("nt-name") as HTMLInputElement).value.trim();
    const start = startEl.value;
    const end = endEl.value;
    const msg = document.getElementById("nt-msg")!;
    const submit = document.getElementById("nt-submit") as HTMLButtonElement;
    const genOn = (document.getElementById("nt-gen") as HTMLInputElement | null)?.checked ?? false;
    msg.style.display = "none";
    const fail = (text: string) => {
      msg.style.display = "";
      msg.textContent = text;
    };
    if (!name) return fail("Give the trip a name.");
    if (!start || !end) return fail("Pick the first and last day on the calendar.");
    submit.disabled = true;
    try {
      if (genOn) {
        const destination = (document.getElementById("nt-dest") as HTMLInputElement).value.trim();
        if (destination.length < 2) return fail("Say where the trip is going.");
        showWait(true);
        const res = await fetch("/api/trips/generate", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            name, start, end, destination,
            pace: (document.getElementById("nt-pace") as HTMLSelectElement).value,
            interests: (document.getElementById("nt-interests") as HTMLInputElement).value.trim(),
            anchors: collectAnchors(),
          }),
        });
        const data = (await res.json()) as { job?: string; error?: string };
        if (!res.ok || !data.job) {
          showWait(false);
          return fail(data.error ?? "Drafting failed — try again.");
        }
        pollDraft(data.job);
        return;
      }
      const res = await fetch("/api/trips", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ name, start, end }),
      });
      const data = (await res.json()) as { id?: string; error?: string };
      if (res.ok && data.id) {
        location.href = `/trip/${encodeURIComponent(data.id)}`;
        return;
      }
      fail(data.error ?? "Couldn't create the trip.");
    } catch {
      showWait(false);
      fail("Couldn't reach the server.");
    } finally {
      submit.disabled = false;
    }
  });
}

/** Landing page for the link in a password-reset email. */
function renderResetPage() {
  const token = decodeURIComponent(location.hash.slice(1));
  app.innerHTML = `
    ${genericHeroHTML()}
    <main>${resetPageHTML()}</main>
    <footer>Get A Map</footer>`;
  wireResetPage(token, () => {
    location.href = "/";
  });
}

function renderAccessPage(tripId: string, deniedWithKey: boolean) {
  app.innerHTML = `
    ${genericHeroHTML()}
    <main>
      <div class="booklist" style="padding:26px 24px;max-width:520px;margin:0 auto">
        <h3>🔒 This trip needs its key</h3>
        <div style="padding:14px 2px; font-size:14px; line-height:1.7">
          ${
            deniedWithKey
              ? "The key in your link didn't unlock this trip — ask your co-planner to re-send the full link, or sign in if the trip is saved to your account."
              : "Your link is missing its secret key (the part after <b>#</b>). Open the complete link you were sent, or sign in."
          }
          <div style="margin:16px 0 8px;font-weight:600">Signed-in members can open the trip without the key:</div>
          <div id="acctpanel">${accountPanelHTML()}</div>
          <div id="signin" style="margin-top:16px"></div>
          <div style="margin-top:16px"><a href="/">← Home</a></div>
        </div>
      </div>
    </main>
    <footer>Get A Map</footer>`;
  wireAccountPanel(() => location.reload());
  void fillProviderButtons("signin");
}

function wireDelegation() {
  // slot indexes are numbers for definition rows, "x1"… for extra rows
  const slotIdx = (el: HTMLElement): number | string =>
    /^x\d+$/.test(el.dataset.i ?? "") ? el.dataset.i! : Number(el.dataset.i);
  document.addEventListener("click", (e) => {
    const el = (e.target as HTMLElement).closest<HTMLElement>("[data-action]");
    if (!el) return;
    switch (el.dataset.action) {
      case "modal": openModal(el.dataset.id!); break;
      case "daymap": openDayMap(el.dataset.day!); break;
      case "directions": openDirections(el.dataset.day!, slotIdx(el)); break;
      case "dir-mode": switchDirectionsMode(el.dataset.mode as any); break;
      case "close": closeModal(); clearDirections(); break;
      case "st": onSt(el.dataset.day!, slotIdx(el)); break;
      case "wanderlog": exportWanderlog(); break;
      case "changelog": toggleChangelog(); break;
      case "print": printItinerary(); break;
      case "fit": {
        const n = onFitDay(el.dataset.day!) ?? 0;
        banner(n ? `Adjusted ${n} time block${n > 1 ? "s" : ""} to fit the target.` : "Already as close to the target as the options allow.", "ok");
        break;
      }
      case "signin": {
        // Come back to where sign-in was started, EXCEPT on the signed-out
        // home screen, where "back to /" is the trip list and is the point.
        const back = location.pathname + location.hash;
        location.href = `/auth/login/${el.dataset.provider}?return=${encodeURIComponent(back || "/")}`;
        break;
      }
      case "signout":
        fetch("/auth/logout", { method: "POST" }).then(() => location.reload());
        break;
      case "avatar-toggle":
        toggleAvatarMenu();
        break;
      case "account":
        document.getElementById("avatar-root")?.classList.remove("open");
        if (document.getElementById("modal")) {
          openAccountModal(() => {
            closeModal();
            // reload rather than repaint: signing in can upgrade an edit-key
            // holder from view-only to editing, and role wiring happens at boot
            location.reload();
          });
        } else {
          // no modal shell on this page — the sign-in form is on the home page
          location.href = "/";
        }
        break;
      case "savetrip":
        // the key in the URL is what proves the right to claim it
        fetch(`/api/trip/${encodeURIComponent(store.tripId)}/join`, {
          method: "POST",
          headers: store.key ? { "x-trip-key": store.key } : {},
        })
          .then((r) => {
            banner(r.ok ? "Saved — this trip is on your account now." : "Couldn't save this trip.", r.ok ? "ok" : "warn");
            document.getElementById("avatar-root")?.classList.remove("open");
            if (r.ok) void renderAvatarMenu(store.tripId || undefined);
          })
          .catch(() => banner("Couldn't reach the server.", "warn"));
        break;
      case "blflag":
        if (store.role === "edit" && el.dataset.k) onChecklistToggle(el.dataset.k, !store.checks[el.dataset.k]);
        break;
      case "activities": openActivitiesPanel(); break;
      case "act-pick": {
        if (store.role !== "edit") break; // the button is disabled, but never rely on that alone
        const dayId = el.dataset.day!;
        const slotIndex = slotIdx(el);
        const day = store.def.days.find((d) => d.id === dayId);
        if (!day) break;
        const item = typeof slotIndex === "number" ? day.items[slotIndex] : undefined;
        // An extra row ("x1") has no definition item, so its options come from
        // the engine — which is the whole point of comp.extra.opts. Reading
        // them off the (absent) item is what left extra rows with an empty
        // picker.
        const opts =
          item && !isFixed(item) ? item.opts : (currentTrip().days[dayId]?.extra?.opts ?? []);
        openActivityPicker({
          dayId,
          slotIndex,
          activityIds: opts,
          currentActId: store.slots[`${dayId}_${slotIndex}`]?.act,
          slotLabel: item && !isFixed(item) ? item.s : "Extra time",
        });
        break;
      }
      case "act-choose":
        // the card carries its own slot — nothing is captured, so a stale
        // picker can never write to the slot it was opened on
        if (store.role === "edit" && el.dataset.act) {
          choosePickerActivity(el.dataset.day!, slotIdx(el), el.dataset.act);
        }
        break;
      case "ext-link":
        // a website link inside a picker card: stop the card's act-choose
        // from also firing (closest() found the anchor first) and let the
        // browser follow the href normally
        break;
      case "theme": {
        const next = themeNow() ? "light" : "dark";
        document.documentElement.dataset.theme = next;
        try { localStorage.setItem("theme", next); } catch { /* private mode: theme just won't persist */ }
        document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')!.content = next === "dark" ? "#0f151c" : "#16344f";
        el.innerHTML = next === "dark" ? ico.sun : ico.moon;
        break;
      }
      case "ed-toggle": {
        const on = toggleEditing();
        el.classList.toggle("on", on);
        el.textContent = on ? "✓ Done editing" : "✎ Edit";
        document.querySelectorAll<HTMLElement>(".ed-only").forEach((b) => (b.style.display = on ? "" : "none"));
        renderAll();
        break;
      }
      case "ed-day-add": openDayForm(null); break;
      case "ed-day-edit": openDayForm(el.dataset.day!); break;
      case "ed-day-move": void moveDay(el.dataset.day!, Number(el.dataset.dir)); break;
      case "ed-item-add": openItemForm(el.dataset.day!, null); break;
      case "ed-item-edit": openItemForm(el.dataset.day!, Number(el.dataset.i)); break;
      case "ed-item-move": void moveItem(el.dataset.day!, Number(el.dataset.i), Number(el.dataset.dir)); break;
      case "ed-item-remove": void removeItem(el.dataset.day!, Number(el.dataset.i)); break;
      case "ed-act-new": openActForm(null); break;
      case "ed-act-edit": openActForm(el.dataset.id!); break;
      case "ed-lodgings": openLodgings(); break;
      case "ed-stay-add": openStayForm(null); break;
      case "ed-stay-edit": openStayForm(el.dataset.id!); break;
      case "ed-stay-remove": void removeStay(el.dataset.id!); break;
      case "ed-bl-add": openBlForm(null); break;
      case "ed-bl-edit": openBlForm(el.dataset.k!); break;
      case "ed-trip-set": openTripSettings(); break;
      case "ai-suggest": openAiSuggest(el.dataset.day!); break;
      case "ai-add": void addSelectedProposals(el.dataset.day!); break;
      case "ai-import": openAiImport(); break;
      case "ai-import-add": void addSelectedImports(); break;
      case "act-remove": void removeActivity(el.dataset.id!); break;
      case "act-unoffer": void unofferActivity(el.dataset.id!, el.dataset.day!, Number(el.dataset.i)); break;
      case "share": openShare(); break;
      case "share-copy": copyShareLink(); break;
      case "share-revoke": revokeShareKey(el.dataset.id!, el.dataset.label!); break;
      case "trip-open": location.href = `/trip/${el.dataset.id!}`; break;
      case "trip-invite":
        // the share modal needs the trip's own page (store, keys, ownership
        // check) — open it with a flag that auto-opens Share on arrival
        location.href = `/trip/${el.dataset.id!}?share=1`;
        break;
      case "trip-rename": {
        const id = decodeURIComponent(el.dataset.id!);
        const name = prompt("New trip name:", el.dataset.name ?? "");
        if (!name?.trim()) break;
        void fetch(`/api/trip/${encodeURIComponent(id)}`, {
          method: "PATCH",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ name: name.trim() }),
        }).then(async (r) => {
          if (r.ok) location.reload();
          else alert((await r.json().catch(() => ({}))).error ?? "Rename failed.");
        });
        break;
      }
      case "trip-delete": {
        // A 3-second armed countdown replaces typed confirmation: the button
        // stays disabled and counts down before it can actually fire, so a
        // stray double-click can't delete a shared trip for everyone.
        const actions = el.closest<HTMLElement>(".trip-tile-actions");
        if (!actions) break;
        const id = el.dataset.id!;
        const name = el.dataset.name!;
        actions.innerHTML = `
          <span class="trip-del-warn">Delete permanently?</span>
          <button type="button" class="btn-det btn-sm" data-action="trip-delete-cancel">Cancel</button>
          <button type="button" class="btn-det btn-sm btn-danger-arm" data-action="trip-delete-confirm" data-id="${id}" data-name="${esc(name)}" disabled>Delete in 3…</button>`;
        const btn = actions.querySelector<HTMLButtonElement>("[data-action='trip-delete-confirm']")!;
        let left = 3;
        const tick = setInterval(() => {
          left -= 1;
          if (left <= 0) {
            clearInterval(tick);
            btn.disabled = false;
            btn.textContent = "✕ Delete now";
          } else {
            btn.textContent = `Delete in ${left}…`;
          }
        }, 1000);
        break;
      }
      case "trip-delete-cancel": {
        const el2 = (e.target as HTMLElement).closest<HTMLElement>(".trip-tile");
        const actions = el2?.querySelector<HTMLElement>(".trip-tile-actions");
        const id = el2?.dataset.id;
        if (!actions || !id) break;
        const name = esc(el2!.dataset.name ?? "");
        actions.innerHTML = `
          <button type="button" class="btn-det btn-sm" data-action="trip-rename" data-id="${id}" data-name="${name}">✎ Rename</button>
          <button type="button" class="btn-det btn-sm" data-action="trip-delete" data-id="${id}" data-name="${name}">✕ Delete</button>`;
        break;
      }
      case "trip-delete-confirm": {
        const id = decodeURIComponent(el.dataset.id!);
        void fetch(`/api/trip/${encodeURIComponent(id)}`, {
          method: "DELETE",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ confirm: id }),
        }).then(async (r) => {
          if (r.ok) location.reload();
          else alert((await r.json().catch(() => ({}))).error ?? "Delete failed.");
        });
        break;
      }
    }
  });
  document.addEventListener("change", (e) => {
    const el = e.target as HTMLElement;
    switch (el.dataset.action) {
      case "act-offer": {
        const v = (el as HTMLSelectElement).value;
        if (v) void offerActivity(el.dataset.id!, v);
        break;
      }
      case "dur": onDur(el.dataset.day!, slotIdx(el), (el as HTMLSelectElement).value); break;
      case "bl": onChecklistToggle(el.dataset.k!, (el as HTMLInputElement).checked); break;
      case "target": {
        const n = onDayTarget(el.dataset.day!, Number((el as HTMLSelectElement).value)) ?? 0;
        banner(n ? `New target set — adjusted ${n} time block${n > 1 ? "s" : ""}.` : "New target set.", "ok");
        break;
      }
      case "fixdur": onFixDur(el.dataset.day!, Number(el.dataset.i), Number((el as HTMLSelectElement).value)); break;
      case "ed-home": void setDayHome(el.dataset.day!); break;
    }
  });
  document.getElementById("modalbg")?.addEventListener("click", (e) => {
    if ((e.target as HTMLElement).id === "modalbg") {
      closeModal();
      clearDirections();
    }
  });
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") {
      closeModal();
      clearDirections();
      return;
    }
    // picker cards are divs (they contain real links, which a <button> may
    // not) — give them a button's keyboard behavior
    if (e.key === "Enter" || e.key === " ") {
      const el = (e.target as HTMLElement).closest?.<HTMLElement>("[role='button'][data-action]");
      if (el && !(el instanceof HTMLButtonElement) && !(el instanceof HTMLAnchorElement)) {
        e.preventDefault();
        el.click();
      }
    }
  });
  // print: expand every accordion, restore after
  const openStates: boolean[] = [];
  window.addEventListener("beforeprint", () => {
    document.querySelectorAll("details").forEach((d, i) => {
      openStates[i] = d.open;
      d.open = true;
    });
  });
  window.addEventListener("afterprint", () => {
    document.querySelectorAll("details").forEach((d, i) => (d.open = openStates[i] ?? d.open));
  });
}

function renderAll() {
  // the hero lives outside #days, so a def resync must repaint it explicitly
  // (trip.set hero edits would otherwise only show after a full reload)
  const hero = document.querySelector(".hero");
  if (hero) hero.outerHTML = heroHTML(store.def.hero, store.tripName);
  buildDayNav();
  renderChecklist();
  buildDays();
  recomputeAndRender();
  initTripMap();
  initDayNavSpy();
  initReveal();
  if (store.role !== "edit") {
    document.body.classList.add("readonly");
    banner("View-only — you'll see live updates. Invited to edit? Sign in (top right) to unlock editing.", "");
  }
  // Managing links needs ownership as well as edit rights, which only the
  // server knows — so ask, and leave the button hidden if the answer is no.
  void canShare().then((ok) => {
    const btn = document.getElementById("sharebtn");
    if (btn && ok) btn.style.display = "";
  });
  void renderAvatarMenu(store.tripId);
  if (store.role === "edit") {
    void checkAiEnabled().then((on) => {
      if (on) document.querySelectorAll<HTMLElement>(".aibtn").forEach((b) => (b.style.display = ""));
    });
    const editBtn = document.getElementById("editbtn");
    if (editBtn) {
      editBtn.style.display = "";
      if (isEditing()) {
        editBtn.classList.add("on");
        editBtn.textContent = "✓ Done editing";
      }
    }
  }
}

async function fetchTrip(tripId: string, key: string | null): Promise<TripResponse | null> {
  const res = await fetch(`/api/trip/${encodeURIComponent(tripId)}`, {
    headers: key ? { "x-trip-key": key } : {},
  });
  if (!res.ok) return null;
  return (await res.json()) as TripResponse;
}

async function boot() {
  wireDelegation();
  void renderAvatarMenu();
  if (location.pathname === "/reset") {
    renderResetPage();
    return;
  }
  if (location.pathname === "/profile") {
    void renderProfile();
    return;
  }
  if (location.pathname === "/new") {
    void renderNewTrip();
    return;
  }
  if (location.pathname === "/admin") {
    void import("./ui/admin.js").then(({ renderAdmin }) => renderAdmin(app, genericHeroHTML()));
    return;
  }
  let tripId: string;
  let key: string | null;
  if (location.pathname === "/s") {
    // Opaque share link: /s#key carries no trip identity in the URL. The key
    // resolves to a trip id server-side (header, never a query string).
    key = location.hash.length > 1 ? location.hash.slice(1) : null;
    const info = key
      ? await fetch("/api/keyinfo", { headers: { "x-trip-key": key } })
          .then((r) => (r.ok ? (r.json() as Promise<{ tripId: string }>) : null))
          .catch(() => null)
      : null;
    if (!info) {
      renderAccessPage("", !!key);
      return;
    }
    tripId = info.tripId;
    localStorage.setItem(`tripkey:${tripId}`, key!);
  } else {
    const m = location.pathname.match(/^\/trip\/([\w-]+)/);
    if (!m) {
      renderHome();
      return;
    }
    tripId = m[1];
    key = location.hash.length > 1 ? location.hash.slice(1) : null;
    if (key) localStorage.setItem(`tripkey:${tripId}`, key);
    else key = localStorage.getItem(`tripkey:${tripId}`);
  }

  let trip: TripResponse | null = null;
  try {
    trip = await fetchTrip(tripId, key);
  } catch {
    /* offline: the PWA shell may still have a cached copy via the SW */
  }
  if (!trip) {
    renderAccessPage(tripId, !!key);
    return;
  }

  // seed defaults for every slot, then overlay the server state (a slot the
  // server has never seen — e.g. added after import — keeps its default)
  const slots = applySavedSlots(trip.definition, seedSlots(trip.definition), trip.state.slots);
  store.init(tripId, key, trip.definition, slots, trip.state.checks, trip.state.dayEnds ?? {}, trip.state.fixDurs ?? {}, trip.rev, trip.role, trip.name, trip.editor);
  renderShell();
  renderAll();

  // arrived via the trip list's "Invite" button: open the share modal once
  // the page is up (ownership still gates it — canShare asks the server)
  if (new URLSearchParams(location.search).get("share") === "1") {
    history.replaceState(null, "", location.pathname + location.hash);
    void canShare().then((ok) => {
      if (ok) openShare();
      else banner("Only the trip's owner can manage invite links.", "warn");
    });
  }

  store.subscribe((evt) => {
    for (const k of evt.slotKeys) {
      const us = k.indexOf("_");
      const idx = k.slice(us + 1);
      // extra slots are keyed d13_x1 — Number("x1") is NaN, which silently
      // skipped the repaint and left a co-planner's change invisible
      refreshSlot(k.slice(0, us), /^x\d+$/.test(idx) ? idx : Number(idx));
    }
    if (evt.dayEndsChanged) {
      for (const [dayId, min] of Object.entries(store.dayEnds)) {
        const sel = document.getElementById(`target_${dayId}`) as HTMLSelectElement | null;
        if (sel && Number(sel.value) !== min) sel.value = String(min);
      }
    }
    if (evt.fixDursChanged) {
      for (const [key, min] of Object.entries(store.fixDurs)) {
        const sel = document.getElementById(`fixdur_${key}`) as HTMLSelectElement | null;
        if (sel && Number(sel.value) !== min) sel.value = String(min);
      }
    }
    if (evt.slotKeys.length || evt.checksChanged || evt.dayEndsChanged || evt.fixDursChanged) {
      invalidateTrip(); // state moved — nothing may read the previous computation
      recomputeAndRender();
      renderChecklist();
    }
    if (evt.remote && evt.editor && evt.editor !== (store.editor || "someone") && evt.slotKeys.length) {
      banner(`${evt.editor}: ${describeChange({ kind: "slot", key: evt.slotKeys[0], newValue: store.slots[evt.slotKeys[0]], oldValue: null, editor: evt.editor, rev: store.rev, at: "" })}`, "ok");
    }
  });

  onStatus(({ online, presence }) => {
    renderStatus(online, presence);
    document.body.classList.toggle("readonly", store.role !== "edit" || !online);
  });
  onResync(async () => {
    const fresh = await fetchTrip(tripId, key);
    if (fresh) {
      const freshSlots = applySavedSlots(fresh.definition, seedSlots(fresh.definition), fresh.state.slots);
      store.init(tripId, key, fresh.definition, freshSlots, fresh.state.checks, fresh.state.dayEnds ?? {}, fresh.state.fixDurs ?? {}, fresh.rev, fresh.role, fresh.name, fresh.editor);
      renderAll();
    }
  });
  connect();

}

// debug hook: lets the print document be inspected without a print dialog
(window as any).__buildPrintDocument = buildPrintDocument;

boot();

// PWA: register the service worker (production builds only)
if (!import.meta.env.DEV && "serviceWorker" in navigator) {
  import("virtual:pwa-register").then(({ registerSW }) => registerSW({ immediate: true })).catch(() => {});
}
